import { capitalize } from 'lodash';
import toast from 'react-hot-toast';
import { NETWORKS } from 'src/common/types/network';
import { Project } from 'src/modules/projects/domain';
import { extractDuneQueryId } from 'src/modules/projects/utils';
import { parseEther } from 'viem';

import { maskAddressValue } from '@/utils/format';
import { extractXPostId } from '@/utils/regex';

import {
  AmbientTradeTriggerFormValues,
  AquariusSwapsTriggerFormValues,
  AquariusTriggerFormValues,
  BalancerTriggerFormValues,
  BlendPositionSide,
  BlendTriggerFormValues,
  buildUtilizationCondition,
  buildUtilizationConditionUpdate,
  ConfigureTriggerFormValues,
  CurrencyArgumentType,
  DiscordRoleAssignedTriggerFormValues,
  DiscordServerJoinTriggerFormValues,
  DuneTriggerFormValues,
  EditTriggerFormValues,
  EulerV2LoopingTriggerFormValues,
  EulerVaultsTriggerFormValues,
  EventTriggerType,
  EventType,
  EXPONENT_VENUE_LABELS,
  ExponentTriggerFormValues,
  FelixCdpCollateralTriggerFormValues,
  FelixVanillaCollateralTriggerFormValues,
  FilterMeetCondition,
  FogoIgnitionLockedTriggerFormValues,
  FogoLendStrategy,
  FogoLendTriggerFormValues,
  FogoTokenHoldersTriggerFormValues,
  GalxeTriggerFormValues,
  GearboxTriggerFormValues,
  HibachiAffiliatesTriggerFormValues,
  HibachiTradingTriggerFormValues,
  HyperliquidTradeTriggerFormValues,
  HypurrCardOperationType,
  HypurrCardTransactionsTriggerFormValues,
  HypurrEulerNetPositionsStrategy,
  HypurrEulerNetPositionsTriggerFormValues,
  HypurrNetPoolPositionsStrategy,
  HypurrNetPoolPositionsTriggerFormValues,
  InsertSmartContractsFormValues,
  LiquidityV3Strategy,
  LiquidityV3TriggerFormValues,
  LiquidityV4TriggerFormValues,
  LynxTradeTriggerFormValues,
  MoneyMarketPositionType,
  MoneyMarketTriggerFormValues,
  MorphoBorrowerStrategyTriggerFormValues,
  MorphoBorrowerStrategyType,
  MorphoTriggerFormValues,
  MorphoVaultsTriggerFormValues,
  NadoTradeTriggerFormValues,
  OdysseyBuilderTradesTriggerFormValues,
  OffChainTriggerAdvancedFilter,
  OffChainTriggerFormValues,
  OnChainTriggerAdvancedFilter,
  OnChainTriggerFormValues,
  OrderlyBuilderTradesTriggerFormValues,
  PacificaBuilderTradesTriggerFormValues,
  PendleLPTriggerFormValues,
  PendleYTTriggerFormValues,
  PolymarketTradeVolumeTriggerFormValues,
  PyronTriggerFormValues,
  RevenueArgumentType,
  SolanaTokenHoldersTriggerFormValues,
  SpectraLPTriggerFormValues,
  SpectraYTTriggerFormValues,
  StellarTokenHoldersTriggerFormValues,
  STRIPE_PAYMENT_EVENT_TYPE_LABELS,
  StripePaymentTriggerFormValues,
  SubgraphBalanceTriggerFormValues,
  SuiTokenHoldersTriggerFormValues,
  TokenHoldersTriggerFormValues,
  UintArgumentUnitType,
  UpdateTriggerExpressionFormValues,
  ValiantLiquidityTriggerFormValues,
  ValiantTradeTriggerFormValues,
  VolumeArgumentType,
  XFollowTriggerFormValues,
  XLikeTriggerFormValues,
  XPostTriggerFormValues,
  XRepostTriggerFormValues,
  XTriggerAction,
  ZealyTriggerFormValues,
} from '../domain';
import {
  AmbientVolumeType,
  GEARBOX_MODE_LABELS,
  GearboxMode,
  LiquidLocker,
  LiquidLockerDTO,
  OrderlyRevenueType,
  OrderlyVolumeType,
  TriggerContext,
  TriggerType,
} from '../types';
import { CreateContractsDTO, CreateTriggerDTO, UpdateTriggerDTO } from './dtos';

const buildConditionExpression = (
  meetCondition: FilterMeetCondition,
  filters?: OnChainTriggerAdvancedFilter[] | OffChainTriggerAdvancedFilter[],
): string | undefined => {
  if (!filters || filters.length === 0) {
    return undefined;
  }

  const expressionJoiner = meetCondition === FilterMeetCondition.ALL ? 'AND' : 'OR';

  const expression = filters
    .map((filter) => {
      const { argument, operator, value, type, unit } = filter;

      if (!argument || !operator || (!value && !type)) {
        return '';
      }

      let formattedValue;

      if (type.includes('int')) {
        formattedValue = unit === UintArgumentUnitType.Wei ? value.toString() : parseEther(value.toString());
      } else if (type.includes('string')) {
        formattedValue = `'${value.toString().toLowerCase()}'`;
      } else {
        formattedValue = value.toString().toLowerCase();
      }

      return `:${argument} ${operator} ${formattedValue}`;
    })
    .join(` ${expressionJoiner} `);

  if (expression === '') {
    return undefined;
  }

  return expression;
};

const buildEventType = (triggerDraft: OnChainTriggerFormValues): EventType => {
  if (triggerDraft.type === EventType.OnChainFunction) {
    return EventType.OnChainFunction;
  }

  return EventType.OnChainEvent;
};

const buildAmountExpression = (
  type: VolumeArgumentType | undefined,
  percentage: number | undefined,
  value: string | undefined,
): string | undefined => {
  if (type === 'noValueAssociated' || !type) {
    return undefined;
  }

  return type === 'fromTriggerArgument' ? value : `${Number(percentage) / 100} * ${value}`;
};

const buildRevenueExpression = (
  type: RevenueArgumentType | undefined,
  percentage: number | undefined,
  value: string | undefined,
): string | undefined => {
  if (type === 'noRevenueAssociated' || !type) {
    return undefined;
  }

  return type === 'fromTriggerArgument' ? value : `${Number(percentage) / 100} * ${value}`;
};

const buildCurrencyExpression = (
  type: CurrencyArgumentType | undefined,
  value: string | undefined,
): string | undefined => {
  if (!type) {
    return undefined;
  }

  return value;
};

export const mapToTriggerDTO = (
  triggerType: EventTriggerType,
  triggerDraft: OnChainTriggerFormValues | OffChainTriggerFormValues,
  contractIds?: string[],
  configureTriggerFormValues?: ConfigureTriggerFormValues,
): CreateTriggerDTO => {
  const amount_expression = buildAmountExpression(
    configureTriggerFormValues?.volumeArgument.type,
    configureTriggerFormValues?.volumeArgument.percentage,
    configureTriggerFormValues?.volumeArgument.value,
  );

  const revenue_expression = buildRevenueExpression(
    configureTriggerFormValues?.revenueArgument.type,
    configureTriggerFormValues?.revenueArgument.percentage,
    configureTriggerFormValues?.revenueArgument.value,
  );

  const volume_currency_expression = buildCurrencyExpression(
    configureTriggerFormValues?.currencyVolumeArgument.type,
    configureTriggerFormValues?.currencyVolumeArgument.value,
  );

  const revenue_currency_expression = buildCurrencyExpression(
    configureTriggerFormValues?.currencyRevenueArgument.type,
    configureTriggerFormValues?.currencyRevenueArgument.value,
  );

  return {
    name: triggerDraft.name,
    description: triggerDraft.description,
    signature: triggerDraft.signature,
    type: TriggerType.Custom,
    event_type:
      triggerType === EventTriggerType.CustomEvent ||
      triggerType === EventTriggerType.Github ||
      triggerType === EventTriggerType.Discord
        ? EventType.OffChainEvent
        : buildEventType(triggerDraft as OnChainTriggerFormValues),
    condition_expression: buildConditionExpression(triggerDraft.filterMeetCondition, triggerDraft.filters),
    amount_expression,
    revenue_expression,
    volume_currency_expression,
    revenue_currency_expression,
    payable: true,
    contract_ids: contractIds,
    end_user_identifier_property: triggerDraft.endUserIdentifierProperty,
    end_user_identifier_expression: buildEndUserIdentifierExpression(configureTriggerFormValues),
  };
};

const buildEndUserIdentifierExpression = (configureTriggerFormValues?: ConfigureTriggerFormValues): string | null => {
  const expression = configureTriggerFormValues?.endUserIdentifierExpression;

  if (!expression || expression.type === 'from') {
    return null;
  }

  return expression.value;
};

export const mapToCreateContractDTO = (contractDraft: InsertSmartContractsFormValues): CreateContractsDTO => ({
  contracts: [{ chainId: contractDraft.contract.chainId, address: contractDraft.contract.contractAddress }],
});

export const mapToTokenHoldersTriggerDTO = (formValues: TokenHoldersTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const name = formValues.name;
  const description = `Daily holding of ${name} on ${networkName}`;

  const volumeCurrencyExpression = formValues.useTokenCurrencyReference
    ? formValues.tokenCurrency
    : formValues.tokenAddress;

  return {
    name,
    description,
    type: TriggerType.TokenHolder,
    context: {
      token_address: formValues.tokenAddress,
      chain_id: formValues.chainId,
      volume_currency_expression: volumeCurrencyExpression,
    },
  };
};

export const mapToSuiTokenHoldersTriggerDTO = (formValues: SuiTokenHoldersTriggerFormValues): CreateTriggerDTO => {
  const maskedAddress = maskAddressValue(formValues.tokenAddress);
  const name = `Hold SUI Token ${maskedAddress}`;
  const description = `Daily holding of SUI token ${formValues.tokenAddress}`;

  const signature = `SUI-TokenHolders-${formValues.tokenAddress}`;

  return {
    name,
    description,
    type: TriggerType.Custom,
    event_type: EventType.OffChainEvent,
    signature,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToSolanaTokenHoldersTriggerDTO = (
  formValues: SolanaTokenHoldersTriggerFormValues,
): CreateTriggerDTO => {
  const maskAddress = maskAddressValue(formValues.tokenAddress);
  const tokenName = formValues.name || maskAddress;
  const name = `Hold ${tokenName} - Solana`;
  const description = `Daily holding of Solana token ${maskAddress}`;

  const volumeCurrencyExpression =
    formValues.useTokenCurrencyReference && formValues.tokenCurrency ? formValues.tokenCurrency : undefined;

  return {
    name,
    description,
    type: TriggerType.SolanaTokenHolder,
    context: {
      token_address: formValues.tokenAddress,
      volume_currency_expression: volumeCurrencyExpression,
      chain_identifier: '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp',
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToFogoTokenHoldersTriggerDTO = (formValues: FogoTokenHoldersTriggerFormValues): CreateTriggerDTO => {
  const maskAddress = maskAddressValue(formValues.tokenAddress);
  const tokenName = formValues.name || maskAddress;
  const name = `Hold ${tokenName} - Fogo`;
  const description = `Daily holding of Fogo token ${maskAddress}`;

  // volume_currency_expression is required - use price reference token if selected, otherwise use token address
  const volumeCurrencyExpression = formValues.useTokenCurrencyReference
    ? formValues.tokenCurrency
    : formValues.tokenAddress;

  return {
    name,
    description,
    type: TriggerType.FogoTokenHolder,
    context: {
      token_address: formValues.tokenAddress,
      volume_currency_expression: volumeCurrencyExpression,
      chain_identifier: '102',
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToStellarTokenHoldersTriggerDTO = (
  formValues: StellarTokenHoldersTriggerFormValues,
): CreateTriggerDTO => {
  const maskAddress = maskAddressValue(formValues.tokenAddress);
  const tokenName = formValues.name || maskAddress;
  const name = `Hold ${tokenName} - Stellar`;
  const description = `Daily holding of Stellar token ${maskAddress}`;

  // volume_currency_expression is required - use price reference token if selected, otherwise use token address
  const volumeCurrencyExpression = formValues.useTokenCurrencyReference
    ? formValues.tokenCurrency
    : formValues.tokenAddress;

  return {
    name,
    description,
    type: TriggerType.StellarTokenHolder,
    context: {
      token_address: formValues.tokenAddress,
      volume_currency_expression: volumeCurrencyExpression,
      // Pinned: the server preset rejects anything else, and a mismatch here leaves every
      // execution rejected as unsupported_volume_currency with no error anywhere (FUU-1945).
      chain_identifier: '148',
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToV2LiquidityTriggerDTO = (formValues: TokenHoldersTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const name = `Hold ${formValues.name} - ${networkName}`;
  const description = `Daily holding of ${name} on ${networkName}`;

  const volumeCurrencyExpression = formValues.useTokenCurrencyReference
    ? formValues.tokenCurrency
    : formValues.tokenAddress;

  return {
    name,
    description,
    type: TriggerType.LiquidityPoolV2,
    context: {
      token_address: formValues.tokenAddress,
      chain_id: formValues.chainId,
      volume_currency_expression: volumeCurrencyExpression,
    },
  };
};

export const mapToXFollowTriggerDTO = (formValues: XFollowTriggerFormValues): CreateTriggerDTO => {
  const { usernameToFollow } = formValues;

  return {
    name: `Follow @${usernameToFollow} on X`,
    description: 'Follow on X',
    type: TriggerType.FollowOnX,
    context: {
      usernameToFollow: usernameToFollow,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToXPostTriggerDTO = (formValues: XPostTriggerFormValues): CreateTriggerDTO => {
  const { postText } = formValues;

  const name = `Post on X`;
  const description = `Post on X`;

  return {
    name,
    description,
    type: TriggerType.TwitterVerifyPost,
    context: {
      text: postText,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToDiscordServerJoinTriggerDTO = (formValues: DiscordServerJoinTriggerFormValues): CreateTriggerDTO => {
  const { guildId } = formValues;

  return {
    name: `Join Discord server ${guildId}`,
    description: 'Join Discord server',
    type: TriggerType.DiscordServerJoin,
    context: {
      guild_id: guildId,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToDiscordRoleAssignedTriggerDTO = (
  formValues: DiscordRoleAssignedTriggerFormValues,
): CreateTriggerDTO => {
  const { guildId, roleId } = formValues;

  return {
    name: `Discord role ${roleId} in server ${guildId}`,
    description: 'Discord role assigned',
    type: TriggerType.DiscordRoleAssigned,
    context: {
      guild_id: guildId,
      role_id: roleId,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToXRepostTriggerDTO = (formValues: XRepostTriggerFormValues): CreateTriggerDTO => {
  const { postUrl } = formValues;

  // The fuul-server retweet contract stores only the post id (signature `x-verify-retweet-${postId}`).
  const postId = extractXPostId(postUrl) ?? '';

  const name = `Repost post ${postId} on X`;
  const description = 'Repost on X';

  return {
    name,
    description,
    type: TriggerType.TwitterVerifyRetweet,
    context: {
      postId,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToXLikeTriggerDTO = (formValues: XLikeTriggerFormValues): CreateTriggerDTO => {
  const { postUrl } = formValues;

  // The fuul-server like contract stores only the post id (signature `x-verify-like-${postId}`).
  const postId = extractXPostId(postUrl) ?? '';

  const name = `Like post ${postId} on X`;
  const description = 'Like on X';

  return {
    name,
    description,
    type: TriggerType.TwitterVerifyLike,
    context: {
      postId,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const XAction = {
  [XTriggerAction.Follow]: mapToXFollowTriggerDTO,
  [XTriggerAction.Post]: mapToXPostTriggerDTO,
};

export const getXActionEncoder = (action: XTriggerAction) => {
  const encoder = XAction[action];
  if (!encoder) {
    throw new Error(`No encoder found for X action: ${action}`);
  }
  return encoder;
};

export const mapToSubgraphBalanceTriggerDTO = (formValues: SubgraphBalanceTriggerFormValues): CreateTriggerDTO => {
  const name = `Subgraph ${formValues.subgraphName}`;
  const description = `Subgraph balance of ${formValues.subgraphName}`;

  return {
    name,
    description,
    type: TriggerType.SubgraphBalance,
    context: {
      subgraphName: formValues.subgraphName,
      tokenCurrency: formValues.tokenCurrency || '',
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

const mapToActiveLiquidityContext = (
  formValues: LiquidityV3TriggerFormValues,
  liquidityPoolId?: string,
): TriggerContext => {
  const strategy = formValues.strategy;
  const tokenDenominator = formValues.tokenDenominator;

  return {
    liquidityPoolId: formValues._poolDetails?.id ?? liquidityPoolId,
    strategy,
    activeLiquidityArgs: {
      tokenDenominator: tokenDenominator,
    },
  };
};

const mapToDistributionFormulaContext = (
  formValues: LiquidityV3TriggerFormValues,
  liquidityPoolId?: string,
): TriggerContext => {
  const strategy = formValues.strategy;
  const liquidityShareWeight = (formValues.liquidity as number) / 100;
  const token0ShareWeight = (formValues.token_0 as number) / 100;
  const token1ShareWeight = (formValues.token_1 as number) / 100;
  const inRangeLiquidity = formValues.inRangeLiquidity;

  return {
    liquidityPoolId: formValues._poolDetails?.id ?? liquidityPoolId,
    strategy,
    distributionFormulaArgs: {
      liquidityShareWeight,
      token0ShareWeight,
      token1ShareWeight,
      inRangeLiquidity,
    },
  };
};

const mapToContext: Record<
  LiquidityV3Strategy,
  (formValues: LiquidityV3TriggerFormValues, liquidityPoolId?: string) => TriggerContext
> = {
  [LiquidityV3Strategy.ActiveLiquidity]: mapToActiveLiquidityContext,
  [LiquidityV3Strategy.DistributionFormula]: mapToDistributionFormulaContext,
};

type LiquidityV3TriggerType =
  | TriggerType.HyperswapLiquidityV3
  | TriggerType.HybraLiquidityV3
  | TriggerType.UniswapLiquidityV3
  | TriggerType.PrjXLiquidityV3
  | TriggerType.KittenSwapLiquidityV3
  | TriggerType.UltrasolidLiquidityV3
  | TriggerType.UpheavalLiquidityV3
  | TriggerType.NestLiquidityV3;

export const mapToLiquidityV3TriggerDTO = (
  triggerType: LiquidityV3TriggerType,
  formValues: LiquidityV3TriggerFormValues,
  liquidityPoolId?: string,
): CreateTriggerDTO => {
  const token0symbol = formValues._poolDetails?.token0.symbol;
  const token1symbol = formValues._poolDetails?.token1.symbol;
  const protocolName = formValues.protocolName;

  const name = `${protocolName} ${formValues._poolDetails?.token0.symbol}-${formValues._poolDetails?.token1.symbol}`;
  const description = `Deposit into ${token0symbol}-${token1symbol} pool on ${protocolName}`;
  const endUserIdentifierProperty = formValues.endUserIdentifierProperty;

  const context = mapToContext[formValues.strategy](formValues, liquidityPoolId);

  return {
    name,
    description,
    type: triggerType,
    context: context,
    end_user_identifier_property: endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

/**
 * Unlike V3, this sends no `liquidityPoolId`: a V4 pool has no row to point at. The pool travels as
 * its `bytes32` id in the context, and the backend reads the payout currency off each event, so the
 * token addresses this form resolved for display never leave it.
 */
export const mapToLiquidityV4TriggerDTO = (formValues: LiquidityV4TriggerFormValues): CreateTriggerDTO => {
  const token0symbol = formValues._poolDetails?.token0.symbol;
  const token1symbol = formValues._poolDetails?.token1.symbol;

  return {
    name: `Uniswap v4 ${token0symbol}-${token1symbol}`,
    description: `Provide liquidity in the ${token0symbol}-${token1symbol} pool on Uniswap v4`,
    type: TriggerType.UniswapLiquidityV4,
    context: {
      pool_id: formValues.poolId?.toLowerCase(),
      // The backend validates this with @IsInt, so it must leave as a number whatever shape the
      // form state is in. Null only before the form picks a default; the schema requires it.
      chain_id: formValues.chainId != null ? Number(formValues.chainId) : undefined,
      token_denominator: formValues.tokenDenominator,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToUpdateTriggerExpressions = (
  project: Project,
  triggerId: string,
  values: UpdateTriggerExpressionFormValues,
): UpdateTriggerDTO => {
  const triggerToUpdate = project.triggers.find((t) => t.id === triggerId);

  if (!triggerToUpdate) {
    throw new Error('Trigger not found');
  }

  return {
    id: triggerToUpdate.id,
    volume_expression: buildAmountExpression(
      values.volumeArgument.type,
      values.volumeArgument.percentage,
      values.volumeArgument.value,
    ),
    revenue_expression: buildRevenueExpression(
      values.revenueArgument.type,
      values.revenueArgument.percentage,
      values.revenueArgument.value,
    ),
    volume_currency_expression: buildCurrencyExpression(
      values.currencyVolumeArgument.type,
      values.currencyVolumeArgument.value,
    ),
    revenue_currency_expression: buildCurrencyExpression(
      values.currencyRevenueArgument.type,
      values.currencyRevenueArgument.value,
    ),
  };
};

export const mapToEditTriggerDTO = (trigger: EditTriggerFormValues): UpdateTriggerDTO => {
  const dto: UpdateTriggerDTO = {
    id: trigger.id,
    name: trigger.name,
    description: trigger.description,
    volume_currency_expression: trigger.volume_currency_expression,
  };

  if (trigger.originalUtilizationCondition) {
    const conditionExpression = buildUtilizationConditionUpdate(trigger, trigger.originalUtilizationCondition);
    if (conditionExpression !== undefined) dto.condition_expression = conditionExpression;
  }

  return dto;
};

export const mapToLynxTradeTriggerDTO = (formValues: LynxTradeTriggerFormValues): CreateTriggerDTO => {
  const name = formValues.name;
  const description = `Lynx trade`;

  const expressionJoiner = formValues.filterMeetCondition === FilterMeetCondition.ALL ? 'AND' : 'OR';
  const condition_expression = formValues.filters
    .map((filter) => {
      const { argument, operator, value, type } = filter;
      if (!argument || !operator || (!value && !type)) {
        return '';
      }
      let formattedValue;
      if (type.includes('string')) {
        formattedValue = `'${value.toString().toLowerCase()}'`;
      } else {
        formattedValue = value.toString().toLowerCase();
      }

      return `${argument} ${operator} ${formattedValue}`;
    })
    .join(` ${expressionJoiner} `);

  return {
    name,
    description,
    type: TriggerType.LynxTrade,
    context: {
      poolAddress: formValues.poolAddress,
      condition_expression: condition_expression || undefined,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToNadoTradeTriggerDTO = (
  formValues: NadoTradeTriggerFormValues,
  type: TriggerType.NadoTrade | TriggerType.NadoPurpsTrade = TriggerType.NadoTrade,
): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;
  const isPurps = type === TriggerType.NadoPurpsTrade;
  const name = isPurps ? `Nado Purps Trade - ${networkName}` : `Nado Trade - ${networkName}`;
  const description = isPurps
    ? `Trade on Nado Purps mobile app on ${networkName}`
    : `Trade on Nado protocol on ${networkName}`;

  return {
    name: name,
    description: description,
    type,
    context: {
      name: name,
      description: description,
      chainId: formValues.chainId,
    },
  };
};

export const mapToMorphoTriggerDTO = (formValues: MorphoTriggerFormValues, usdcAddress?: string): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const description = 'Morpho Vault event trigger';

  if (!usdcAddress) {
    toast.error(`Can't resolve USDC address`);
    throw new Error(`Can't resolve USDC address`);
  }

  return {
    name: formValues.name,
    description: description,
    type: TriggerType.Custom,
    event_type: EventType.OnChainSubgraphEvent,
    signature: `SubgraphBalance-Morpho-User-Daily-Fees-${capitalize(networkName)}@${formValues.contractAddress.toLowerCase()}`,
    amount_expression: 'quantity',
    volume_currency_expression: usdcAddress,
    revenue_expression: 'quantity',
    revenue_currency_expression: usdcAddress,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToMorphoVaultsTriggerDTO = (formValues: MorphoVaultsTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;
  const name = `Deposit on Morpho Vault - ${networkName}`;
  const description = 'Deposit on Morpho Vault trigger';

  return {
    name,
    description,
    type: TriggerType.MorphoVaults,
    context: {
      vault: formValues.vault.toLowerCase(),
      chain_id: formValues.chainId,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToEulerVaultsTriggerDTO = (formValues: EulerVaultsTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;
  const name = `Deposit on Euler Vault - ${networkName}`;
  const description = 'Deposit on Euler Vault trigger';

  return {
    name,
    description,
    type: TriggerType.EulerVaults,
    context: {
      vault: formValues.vault.toLowerCase(),
      chain_id: formValues.chainId,
      underlying_address: formValues.underlyingAddress.toLowerCase(),
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToEulerV2LoopingTriggerDTO = (formValues: EulerV2LoopingTriggerFormValues): CreateTriggerDTO => {
  const depositMasked = maskAddressValue(formValues.depositVault) ?? '';
  const borrowMasked = maskAddressValue(formValues.borrowVault) ?? '';
  const name = `Euler V2 Looping - ${depositMasked}/${borrowMasked}`;
  const description = 'Euler V2 looping net deposit trigger';

  const context: TriggerContext = {
    depositVault: formValues.depositVault.toLowerCase(),
    borrowVault: formValues.borrowVault.toLowerCase(),
    chain_id: 1,
    ...(formValues.maxLeverage
      ? {
          max_leverage: parseFloat(formValues.maxLeverage),
          max_leverage_operator: formValues.maxLeverageOperator,
        }
      : {}),
  };

  return {
    name,
    description,
    type: TriggerType.EulerV2Looping,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToDuneQueryTriggerDTO = (formValues: DuneTriggerFormValues): CreateTriggerDTO => {
  const name = formValues.name;
  const description = formValues.description ?? '';
  const duneQueryId = extractDuneQueryId(formValues.queryUrl);

  if (duneQueryId === null) {
    throw new Error('Invalid Dune query URL');
  }

  return {
    name: name,
    description: description,
    type: TriggerType.DuneQuery,
    context: {
      name: name,
      description: description,
      duneQueryId: Number(duneQueryId),
    },
  };
};

export const mapToZealyQuestTriggerDTO = (formValues: ZealyTriggerFormValues): CreateTriggerDTO => {
  const { name, description, questUrl, endUserIdentifierProperty } = formValues;

  const zealyUrlRegex = /^https:\/\/zealy\.io\/cw\/([^\/]+)\/questboard\/[^\/]+\/([^\/]+)$/;
  const match = questUrl.match(zealyUrlRegex);

  let subdomain: string | undefined;
  let quest_id: string | undefined;

  if (match && match[1] && match[2]) {
    subdomain = match[1];
    quest_id = match[2];
  } else {
    console.error('Invalid Zealy Quest URL format:', questUrl);
  }

  return {
    name,
    description,
    type: TriggerType.ZealyQuest,
    context: {
      subdomain: subdomain,
      quest_id: quest_id,
    },
    end_user_identifier_property: endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToGalxeQuestTriggerDTO = (formValues: GalxeTriggerFormValues): CreateTriggerDTO => {
  const { name, description, questUrl, endUserIdentifierProperty } = formValues;

  const galxeUrlRegex = /^https:\/\/app\.galxe\.com\/quest\/[^\/]+\/([^\/]+)$/;
  const match = questUrl.match(galxeUrlRegex);

  let quest_id: string | undefined;

  if (match && match[1]) {
    quest_id = match[1];
  } else {
    console.error('Invalid Galxe Quest URL format:', questUrl);
  }

  return {
    name,
    description,
    type: TriggerType.GalxeQuest,
    context: {
      quest_id: quest_id,
    },
    end_user_identifier_property: endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToPendleYTTriggerDTO = (formValues: PendleYTTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;
  const capitalizedNetworkName = networkName ? networkName.charAt(0).toUpperCase() + networkName.slice(1) : '';

  return {
    name: `Pendle YT: ${maskAddressValue(formValues.ytAddress)} - ${capitalizedNetworkName}`,
    description: `Tracks user balances for Pendle YT: ${formValues.ytAddress} on ${capitalizedNetworkName}`,
    type: TriggerType.PendleYT,
    context: {
      ytAddress: formValues.ytAddress,
      syAddress: formValues.syAddress,
      volume_currency_expression: formValues.volumeCurrencyAddress,
      network: networkName || '',
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

const mapToLiquidLockers = (liquidLockers: LiquidLocker[]): LiquidLockerDTO[] | undefined => {
  // Filter out entries where both fields are empty
  const validLockers = liquidLockers.filter(
    (locker) => locker.address && locker.receiptToken && locker.address.length > 0 && locker.receiptToken.length > 0,
  );

  // If no valid entries, return undefined
  if (validLockers.length === 0) {
    return undefined;
  }

  // Map valid entries to DTO format
  return validLockers.map((locker) => ({
    address: locker.address,
    receipt_token: locker.receiptToken,
  }));
};

export const mapToPendleLPTriggerDTO = (formValues: PendleLPTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  return {
    name: `Pendle LP: ${maskAddressValue(formValues.lpAddress)} - ${networkName}`,
    description: `Tracks user balances for Pendle LP: ${formValues.lpAddress} on ${networkName}`,
    type: TriggerType.PendleLPToken,
    context: {
      contracts: [{ chain_id: formValues.chainId, address: formValues.lpAddress }],
      sy_address: formValues.syAddress,
      price_reference_address: formValues.volumeCurrencyAddress,
      ...(mapToLiquidLockers(formValues.liquidLockers) && {
        liquid_lockers: mapToLiquidLockers(formValues.liquidLockers),
      }),
    },
  };
};

export const mapToSpectraYTTriggerDTO = (formValues: SpectraYTTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;
  const capitalizedNetworkName = networkName ? networkName.charAt(0).toUpperCase() + networkName.slice(1) : '';

  return {
    name: `Spectra YT: ${maskAddressValue(formValues.tokenAddress)} - ${capitalizedNetworkName}`,
    description: `Tracks user balances for Spectra YT: ${formValues.tokenAddress} on ${capitalizedNetworkName}`,
    type: TriggerType.SpectraYT,
    context: {
      token_address: formValues.tokenAddress,
      chain_id: formValues.chainId,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToSpectraLPTriggerDTO = (formValues: SpectraLPTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;
  const capitalizedNetworkName = networkName ? networkName.charAt(0).toUpperCase() + networkName.slice(1) : '';

  return {
    name: `Spectra LP: ${maskAddressValue(formValues.tokenAddress)} - ${capitalizedNetworkName}`,
    description: `Tracks user balances for Spectra LP: ${formValues.tokenAddress} on ${capitalizedNetworkName}`,
    type: TriggerType.SpectraLP,
    context: {
      token_address: formValues.tokenAddress,
      chain_id: formValues.chainId,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToBalancerTriggerDTO = (formValues: BalancerTriggerFormValues): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const name = `Hold Balancer LP ${formValues.name} - ${networkName}`;
  const description = `Daily holding of Balancer LP ${formValues.name} on ${networkName}`;

  const volumeCurrencyExpression = formValues.tokenAddress;

  return {
    name,
    description,
    type: TriggerType.Balancer,
    context: {
      token_address: formValues.tokenAddress,
      chain_id: formValues.chainId,
      volume_currency_expression: volumeCurrencyExpression,
    },
  };
};

const moneyMarketProtocolTranslator: Record<EventTriggerType.CompoundV3, TriggerType> = {
  [EventTriggerType.CompoundV3]: TriggerType.CompoundV3,
};

const mapToCompoundV3Context = (formValues: MoneyMarketTriggerFormValues): TriggerContext => {
  return {
    market_address: formValues.marketAddress,
    market_chain_id: formValues.chainId,
    position_type: formValues.positionType,
  };
};

const mapToContextMoneyMarket: Record<
  EventTriggerType.CompoundV3,
  (formValues: MoneyMarketTriggerFormValues) => TriggerContext
> = {
  [EventTriggerType.CompoundV3]: mapToCompoundV3Context,
};

export const mapToMoneyMarketTriggerDTO = (
  protocol: EventTriggerType.CompoundV3,
  formValues: MoneyMarketTriggerFormValues,
): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const positionTypeLabel = formValues.positionType === MoneyMarketPositionType.Lending ? 'Lending' : 'Borrowing';
  const name = `${positionTypeLabel} ${maskAddressValue(formValues.marketAddress)} - ${networkName}`;
  const description = `${positionTypeLabel} ${formValues.marketAddress} in ${networkName}`;

  const context = mapToContextMoneyMarket[protocol](formValues);

  return {
    name,
    description,
    type: moneyMarketProtocolTranslator[protocol],
    context,
  };
};

export const mapToMorphoBorrowerStrategyDTO = (
  formValues: MorphoBorrowerStrategyTriggerFormValues,
): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const strategyTypeLabel = formValues.strategyType.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const name = `Morpho ${strategyTypeLabel} ${maskAddressValue(formValues.marketId)} - ${networkName}`;
  const description = `Morpho ${strategyTypeLabel} strategy for market ${formValues.marketId} on ${networkName}`;

  const context: TriggerContext = {
    type: formValues.strategyType,
    market_chain_id: formValues.chainId,
    market_address: formValues.marketId,
  };

  // Only add native currency fields if the strategy type requires them
  if (
    formValues.strategyType === MorphoBorrowerStrategyType.BorrowNative ||
    formValues.strategyType === MorphoBorrowerStrategyType.CollateralNative ||
    formValues.strategyType === MorphoBorrowerStrategyType.SupplyNative
  ) {
    if (formValues.nativeCurrencyAddress) {
      context.native_currency_address = formValues.nativeCurrencyAddress;
    }
    if (formValues.nativeCurrencyDecimals) {
      context.native_currency_decimals = formValues.nativeCurrencyDecimals;
    }
  }

  return {
    name,
    description,
    type: TriggerType.MorphoBorrowerStrategy,
    context,
  };
};

export const mapToHypurrNetPoolPositionsDTO = (
  formValues: HypurrNetPoolPositionsTriggerFormValues,
): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const strategyLabel = formValues.strategy === HypurrNetPoolPositionsStrategy.Deposit ? 'Deposit' : 'Borrow';
  const name = `${strategyLabel} ${maskAddressValue(formValues.hyTokenAddress)} - ${maskAddressValue(formValues.variableDebtTokenAddress)}`;
  const description = `${strategyLabel} ${formValues.hyTokenAddress} - ${formValues.variableDebtTokenAddress} in ${networkName}`;

  const context: TriggerContext = {
    strategy: formValues.strategy,
    hyTokenAddress: formValues.hyTokenAddress,
    variableDebtTokenAddress: formValues.variableDebtTokenAddress,
    volumeCurrencyAddress: formValues.volumeCurrencyAddress,
    chainId: formValues.chainId,
  };

  return {
    name,
    description,
    type: TriggerType.HypurrNetPoolPositions,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToHypurrEulerNetPositionsDTO = (
  formValues: HypurrEulerNetPositionsTriggerFormValues,
): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;

  const strategyLabel = formValues.strategy === HypurrEulerNetPositionsStrategy.Deposit ? 'Deposit' : 'Borrow';
  const name = `${strategyLabel} ${maskAddressValue(formValues.depositTokenAddress)} - ${maskAddressValue(formValues.debtTokenAddress)}`;
  const description = `${strategyLabel} ${formValues.depositTokenAddress} - ${formValues.debtTokenAddress} in ${networkName}`;

  const context: TriggerContext = {
    strategy: formValues.strategy,
    depositTokenAddress: formValues.depositTokenAddress,
    debtTokenAddress: formValues.debtTokenAddress,
    volumeCurrencyAddress: formValues.volumeCurrencyAddress,
    chainId: formValues.chainId,
  };

  return {
    name,
    description,
    type: TriggerType.HypurrEulerNetPositions,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToHibachiTradingDTO = (formValues: HibachiTradingTriggerFormValues): CreateTriggerDTO => {
  const name = 'Hibachi Trades';
  const description = 'Trades from Hibachi platform';

  const context: TriggerContext = {
    chainId: 8453, // Base chain ID
  };

  return {
    name,
    description,
    type: TriggerType.HibachiTrading,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToHibachiAffiliatesDTO = (formValues: HibachiAffiliatesTriggerFormValues): CreateTriggerDTO => {
  const levelLabel = formValues.referredFeesLevel.charAt(0).toUpperCase() + formValues.referredFeesLevel.slice(1);

  const name = `Hibachi Affiliates - Base - ${levelLabel}`;
  const description = `Trigger of Hibachi affiliates in Base with ${levelLabel}`;

  const context: TriggerContext = {
    chainId: 8453,
    referredFeesLevel: formValues.referredFeesLevel,
  };

  return {
    name,
    description,
    type: TriggerType.HibachiAffiliateReferredFees,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToOrderlyBuilderTradesDTO = (formValues: OrderlyBuilderTradesTriggerFormValues): CreateTriggerDTO => {
  const name = `Orderly Trades for ${formValues.brokerId}`;

  // Map volume type to human readable description
  const volumeTypeLabels: Record<OrderlyVolumeType, string> = {
    [OrderlyVolumeType.AllVolume]: 'All volume',
    [OrderlyVolumeType.TakerVolume]: 'Taker volume',
    [OrderlyVolumeType.MakerVolume]: 'Maker volume',
  };

  const revenueTypeLabels: Record<OrderlyRevenueType, string> = {
    [OrderlyRevenueType.TotalFee]: 'Total Fee',
    [OrderlyRevenueType.BrokerFee]: 'Broker Fee',
    [OrderlyRevenueType.RealizedPnl]: 'Realized PnL',
  };

  const description = `Orderly Builder Trades - ${volumeTypeLabels[formValues.volumeType] || formValues.volumeType} - ${revenueTypeLabels[formValues.revenueType] || formValues.revenueType}`;

  const context: TriggerContext = {
    brokerId: formValues.brokerId,
    volumeType: formValues.volumeType,
    revenueType: formValues.revenueType,
  };

  return {
    name,
    description,
    type: TriggerType.OrderlyBuilderTrades,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToAmbientTradeDTO = (formValues: AmbientTradeTriggerFormValues): CreateTriggerDTO => {
  const volumeTypeLabels: Record<AmbientVolumeType, string> = {
    [AmbientVolumeType.MakerVolume]: 'Maker Volume',
    [AmbientVolumeType.TakerVolume]: 'Taker Volume',
    [AmbientVolumeType.AllVolume]: 'All Volume',
  };

  const volumeLabel = volumeTypeLabels[formValues.volumeType] || formValues.volumeType;
  const name = `Ambient Trade - ${volumeLabel}`;
  const description = `Ambient trade trigger with ${volumeLabel}`;

  return {
    name,
    description,
    type: TriggerType.AmbientTrade,
    context: {
      volumeType: formValues.volumeType,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToHyperliquidTradeDTO = (formValues: HyperliquidTradeTriggerFormValues): CreateTriggerDTO => {
  const builderNames: Record<string, string> = {
    '0x8af3545a3988b7A46f96F9F1AE40c0e64Fa493C2': 'HyperSignals',
  };

  const builderName = builderNames[formValues.builderAddress] || maskAddressValue(formValues.builderAddress);
  const name = `Hyperliquid Trade - ${builderName}`;
  const description = `Hyperliquid trade trigger - ${builderName} builder.`;

  const context: TriggerContext = {
    builderAddress: formValues.builderAddress,
  };

  return {
    name,
    description,
    type: TriggerType.HyperliquidTrade,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToValiantTradeDTO = (formValues: ValiantTradeTriggerFormValues): CreateTriggerDTO => {
  return {
    name: 'Trade spot on Valiant',
    description: 'Trade spot on Valiant protocol',
    type: TriggerType.ValiantTrade,
    context: {},
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToValiantLiquidityDTO = (formValues: ValiantLiquidityTriggerFormValues): CreateTriggerDTO => {
  return {
    name: 'Provide liquidity on Valiant',
    description: 'Provide liquidity on Valiant protocol',
    type: TriggerType.ValiantLiquidity,
    context: {},
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToPyronTriggerDTO = (formValues: PyronTriggerFormValues): CreateTriggerDTO => {
  const strategyLabel = formValues.strategy === 'Lend' ? 'Lending' : 'Borrowing';
  const name = `${strategyLabel} ${maskAddressValue(formValues.asset)} - Pyron`;
  const description = `${strategyLabel} on Pyron protocol`;

  const context: TriggerContext = {
    asset: formValues.asset,
    market: formValues.market,
    strategy: formValues.strategy,
  };

  return {
    name,
    description,
    type: TriggerType.Pyron,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

const BLEND_SIDE_LABELS: Record<BlendPositionSide, string> = {
  [BlendPositionSide.Supply]: 'Supply',
  [BlendPositionSide.Borrow]: 'Borrow',
};

export const mapToAquariusTriggerDTO = (formValues: AquariusTriggerFormValues): CreateTriggerDTO => {
  const maskedPool = maskAddressValue(formValues.poolAddress);
  const name = `Provide liquidity ${maskedPool} - Aquarius`;
  const description = `Daily liquidity position in Aquarius pool ${maskedPool}`;

  return {
    name,
    description,
    type: TriggerType.AquariusLiquidity,
    context: {
      pool_address: formValues.poolAddress,
      // Pinned: the server preset rejects anything else, and a mismatch here leaves every
      // execution rejected as unsupported_volume_currency with no error anywhere (FUU-1945).
      chain_identifier: '148',
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToAquariusSwapsTriggerDTO = (formValues: AquariusSwapsTriggerFormValues): CreateTriggerDTO => {
  const maskedPool = maskAddressValue(formValues.poolAddress);
  const name = `Swap ${maskedPool} - Aquarius`;
  const description = `Swap volume in Aquarius pool ${maskedPool}`;

  return {
    name,
    description,
    type: TriggerType.AquariusSwaps,
    context: {
      pool_address: formValues.poolAddress,
      // Pinned: any other value leaves every execution rejected as unsupported_volume_currency (FUU-1945).
      chain_identifier: '148',
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToBlendTriggerDTO = (formValues: BlendTriggerFormValues): CreateTriggerDTO => {
  const sideLabel = BLEND_SIDE_LABELS[formValues.type];
  const name = `${sideLabel} ${maskAddressValue(formValues.asset)} - Blend`;
  const description = `${sideLabel} position on Blend pool ${maskAddressValue(formValues.poolAddress)}`;
  const conditionExpression = buildUtilizationCondition(formValues);

  return {
    name,
    description,
    type: TriggerType.BlendPositions,
    context: {
      pool_address: formValues.poolAddress,
      asset: formValues.asset,
      type: formValues.type,
      // Pinned: the server preset rejects anything else, and a mismatch here leaves every
      // execution rejected as unsupported_volume_currency with no error anywhere (FUU-1945).
      chain_identifier: '148',
      // Inside context on purpose: the preset drops a top-level condition_expression on create.
      ...(conditionExpression && { condition_expression: conditionExpression }),
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToExponentTriggerDTO = (formValues: ExponentTriggerFormValues): CreateTriggerDTO => {
  const venueLabel = EXPONENT_VENUE_LABELS[formValues.venue];

  return {
    // Exponent is the only preset whose name the backend post-processes: ExponentTriggerCreator
    // appends ` ${venue} ${truncateAddress(vault)}` to whatever we send here, so the stored name
    // reads `Exponent Clmm 4WnQMQ...XgJh`. Building the full display name here would double it up.
    name: 'Exponent',
    description: `Exponent ${venueLabel} positions on Solana`,
    type: TriggerType.Exponent,
    context: {
      // Base58 is case sensitive and the signature is built from it verbatim — never lowercase it.
      vault_address: formValues.vaultAddress,
      venue: formValues.venue,
    },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToFogoIgnitionLockedDTO = (formValues: FogoIgnitionLockedTriggerFormValues): CreateTriggerDTO => {
  return {
    name: 'Ignition Locked iFOGO',
    description: 'Ignition locked iFOGO position on Fogo',
    type: TriggerType.FogoIgnitionLocked,
    context: { chainIdentifier: 102 },
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToFogoLendTriggerDTO = (formValues: FogoLendTriggerFormValues): CreateTriggerDTO => {
  const strategyLabel = formValues.strategy === FogoLendStrategy.Deposit ? 'Deposit' : 'Borrow';
  const name = `FogoLend ${strategyLabel}`;
  const description = `FogoLend ${strategyLabel} position on Fogo`;

  const context: TriggerContext = {
    market: 'all',
    strategy: formValues.strategy,
  };

  return {
    name,
    description,
    type: TriggerType.FogoLend,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToFelixVanillaCollateralDTO = (
  formValues: FelixVanillaCollateralTriggerFormValues,
): CreateTriggerDTO => {
  const networkName = NETWORKS.find((network) => network.chainId === formValues.chainId)?.name;
  const name = `Felix Vanilla Collateral - Market ${maskAddressValue(formValues.marketId)} - ${networkName}`;
  const description = `Felix Vanilla Collateral trigger for market ${formValues.marketId} on ${networkName}`;

  const context: TriggerContext = {
    marketId: formValues.marketId,
    asset: formValues.asset,
  };

  return {
    name,
    description,
    type: TriggerType.FelixVanillaCollateral,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToFelixCdpCollateralDTO = (formValues: FelixCdpCollateralTriggerFormValues): CreateTriggerDTO => {
  const name = `Felix CDP Collateral - ${maskAddressValue(formValues.collateralAddress)} - HyperEVM`;
  const description = `Felix CDP Collateral trigger for ${formValues.collateralAddress} on HyperEVM`;

  const context: TriggerContext = {
    tokenAddress: formValues.collateralAddress,
  };

  return {
    name,
    description,
    type: TriggerType.FelixCdpCollateral,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToPolymarketTradeVolumeDTO = (formValues: PolymarketTradeVolumeTriggerFormValues): CreateTriggerDTO => {
  const name = formValues.name || 'Polymarket Trade Volume';
  const description = `Polymarket trade volume trigger for condition ${formValues.conditionId}`;

  const context: TriggerContext = {
    conditionId: formValues.conditionId,
  };

  return {
    name,
    description,
    type: TriggerType.PolymarketTradeVolume,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToGearboxTriggerDTO = (formValues: GearboxTriggerFormValues): CreateTriggerDTO => {
  const modeLabel = GEARBOX_MODE_LABELS[formValues.mode];
  const name = `Gearbox ${modeLabel} - ${maskAddressValue(formValues.assetAddress)}`;
  const description = `Gearbox ${modeLabel.toLowerCase()} trigger for asset ${formValues.assetAddress} on Ethereum`;

  const GEARBOX_API_MODE: Record<GearboxMode, string> = {
    [GearboxMode.TotalPosition]: 'total-position',
    [GearboxMode.Supply]: 'supply',
  };
  const apiMode = GEARBOX_API_MODE[formValues.mode];

  const context: TriggerContext = {
    mode: apiMode,
    chain_id: formValues.chainId,
    asset_address: formValues.assetAddress,
  };

  return {
    name,
    description,
    type: TriggerType.Gearbox,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToHypurrCardTransactionsDTO = (
  formValues: HypurrCardTransactionsTriggerFormValues,
): CreateTriggerDTO => {
  const operationLabel = formValues.operationType === HypurrCardOperationType.Borrow ? 'Borrow' : 'Withdraw';
  const name = `HypurrFi Card ${operationLabel}`;
  const description = `HypurrFi card ${operationLabel.toLowerCase()} transaction on HyperEVM`;

  const context: TriggerContext = {
    operationType: formValues.operationType,
    chainId: formValues.chainId,
  };

  return {
    name,
    description,
    type: TriggerType.HypurrCardTransactions,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToStripePaymentDTO = (formValues: StripePaymentTriggerFormValues): CreateTriggerDTO => {
  const eventLabel = STRIPE_PAYMENT_EVENT_TYPE_LABELS[formValues.paymentEventType];
  const name = `Stripe Payment — ${eventLabel}`;
  const description = `Stripe payment trigger for ${eventLabel.toLowerCase()}`;

  const context: TriggerContext = {
    paymentEventType: formValues.paymentEventType,
  };

  return {
    name,
    description,
    type: TriggerType.StripePayment,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToPacificaBuilderTradesDTO = (formValues: PacificaBuilderTradesTriggerFormValues): CreateTriggerDTO => {
  const name = `Pacifica Trades for ${formValues.builderCode}`;
  const description = `Pacifica Builder Trades - Builder Code: ${formValues.builderCode}`;

  const context: TriggerContext = {
    builderCode: formValues.builderCode,
    chainIdentifier: 101,
  };

  return {
    name,
    description,
    type: TriggerType.PacificaBuilderTrades,
    context,
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const mapToOdysseyBuilderTradesDTO = (formValues: OdysseyBuilderTradesTriggerFormValues): CreateTriggerDTO => {
  return {
    name: 'Odyssey Builder Trades',
    description: 'Odyssey Finance builder trades',
    type: TriggerType.OdysseyBuilderTrades,
    context: {},
    end_user_identifier_property: formValues.endUserIdentifierProperty,
    end_user_identifier_expression: null,
  };
};

export const encodeByTriggerType = (
  triggerType: EventTriggerType,
  formValues: Record<string, unknown>,
  usdcAddress?: string,
): CreateTriggerDTO => {
  switch (triggerType) {
    case EventTriggerType.OnChainTokenHolder:
      return mapToTokenHoldersTriggerDTO(formValues as unknown as TokenHoldersTriggerFormValues);

    case EventTriggerType.SuiTokenHolders:
      return mapToSuiTokenHoldersTriggerDTO(formValues as unknown as SuiTokenHoldersTriggerFormValues);

    case EventTriggerType.SolanaTokenHolders:
      return mapToSolanaTokenHoldersTriggerDTO(formValues as unknown as SolanaTokenHoldersTriggerFormValues);

    case EventTriggerType.FogoTokenHolders:
      return mapToFogoTokenHoldersTriggerDTO(formValues as unknown as FogoTokenHoldersTriggerFormValues);

    case EventTriggerType.StellarTokenHolders:
      return mapToStellarTokenHoldersTriggerDTO(formValues as unknown as StellarTokenHoldersTriggerFormValues);

    // Liquidity V3 triggers - all use the same encoder with protocol from formValues
    case EventTriggerType.Uniswap:
    case EventTriggerType.HybraLiquidityV3:
    case EventTriggerType.HyperswapLiquidityV3:
    case EventTriggerType.PrjXLiquidityV3:
    case EventTriggerType.KittenSwapLiquidityV3:
    case EventTriggerType.UltrasolidLiquidityV3:
    case EventTriggerType.UpheavalLiquidityV3:
    case EventTriggerType.NestLiquidityV3:
      return mapToLiquidityV3TriggerDTO(
        formValues._protocol as LiquidityV3TriggerType,
        formValues as unknown as LiquidityV3TriggerFormValues,
      );

    case EventTriggerType.UniswapV4:
      return mapToLiquidityV4TriggerDTO(formValues as unknown as LiquidityV4TriggerFormValues);

    // Other Liquidity triggers
    case EventTriggerType.OnChainV2Liquidity:
      return mapToV2LiquidityTriggerDTO(formValues as unknown as TokenHoldersTriggerFormValues);

    case EventTriggerType.BalancerPool:
      return mapToBalancerTriggerDTO(formValues as unknown as BalancerTriggerFormValues);

    case EventTriggerType.ValiantLiquidity:
      return mapToValiantLiquidityDTO(formValues as unknown as ValiantLiquidityTriggerFormValues);

    // Trading triggers
    case EventTriggerType.LynxTrade:
      return mapToLynxTradeTriggerDTO(formValues as unknown as LynxTradeTriggerFormValues);

    case EventTriggerType.NadoTrade:
      return mapToNadoTradeTriggerDTO(formValues as unknown as NadoTradeTriggerFormValues);

    case EventTriggerType.NadoPurpsTrade:
      return mapToNadoTradeTriggerDTO(formValues as unknown as NadoTradeTriggerFormValues, TriggerType.NadoPurpsTrade);

    case EventTriggerType.HibachiTrading:
      return mapToHibachiTradingDTO(formValues as unknown as HibachiTradingTriggerFormValues);

    case EventTriggerType.HibachiAffiliates:
      return mapToHibachiAffiliatesDTO(formValues as unknown as HibachiAffiliatesTriggerFormValues);

    case EventTriggerType.HyperliquidTrade:
      return mapToHyperliquidTradeDTO(formValues as unknown as HyperliquidTradeTriggerFormValues);

    case EventTriggerType.AmbientTrade:
      return mapToAmbientTradeDTO(formValues as unknown as AmbientTradeTriggerFormValues);

    case EventTriggerType.OrderlyBuilderTrades:
      return mapToOrderlyBuilderTradesDTO(formValues as unknown as OrderlyBuilderTradesTriggerFormValues);

    case EventTriggerType.PacificaBuilderTrades:
      return mapToPacificaBuilderTradesDTO(formValues as unknown as PacificaBuilderTradesTriggerFormValues);

    case EventTriggerType.OdysseyBuilderTrades:
      return mapToOdysseyBuilderTradesDTO(formValues as unknown as OdysseyBuilderTradesTriggerFormValues);

    case EventTriggerType.ValiantTrade:
      return mapToValiantTradeDTO(formValues as unknown as ValiantTradeTriggerFormValues);

    // DeFi / Money Market triggers
    case EventTriggerType.Morpho:
      return mapToMorphoTriggerDTO(formValues as unknown as MorphoTriggerFormValues, usdcAddress);

    case EventTriggerType.CompoundV3:
      return mapToMoneyMarketTriggerDTO(
        EventTriggerType.CompoundV3,
        formValues as unknown as MoneyMarketTriggerFormValues,
      );

    case EventTriggerType.MorphoVaults:
      return mapToMorphoVaultsTriggerDTO(formValues as unknown as MorphoVaultsTriggerFormValues);

    case EventTriggerType.EulerV2Looping:
      return mapToEulerV2LoopingTriggerDTO(formValues as unknown as EulerV2LoopingTriggerFormValues);

    case EventTriggerType.EulerVaults:
      return mapToEulerVaultsTriggerDTO(formValues as unknown as EulerVaultsTriggerFormValues);

    case EventTriggerType.MorphoBorrowerStrategy:
      return mapToMorphoBorrowerStrategyDTO(formValues as unknown as MorphoBorrowerStrategyTriggerFormValues);

    case EventTriggerType.HypurrNetPoolPositions:
      return mapToHypurrNetPoolPositionsDTO(formValues as unknown as HypurrNetPoolPositionsTriggerFormValues);

    case EventTriggerType.HypurrEulerNetPositions:
      return mapToHypurrEulerNetPositionsDTO(formValues as unknown as HypurrEulerNetPositionsTriggerFormValues);

    case EventTriggerType.Pyron:
      return mapToPyronTriggerDTO(formValues as unknown as PyronTriggerFormValues);

    case EventTriggerType.BlendPositions:
      return mapToBlendTriggerDTO(formValues as unknown as BlendTriggerFormValues);

    case EventTriggerType.AquariusLiquidity:
      return mapToAquariusTriggerDTO(formValues as unknown as AquariusTriggerFormValues);

    case EventTriggerType.AquariusSwaps:
      return mapToAquariusSwapsTriggerDTO(formValues as unknown as AquariusSwapsTriggerFormValues);

    case EventTriggerType.Exponent:
      return mapToExponentTriggerDTO(formValues as unknown as ExponentTriggerFormValues);

    case EventTriggerType.FogoLend:
      return mapToFogoLendTriggerDTO(formValues as unknown as FogoLendTriggerFormValues);

    case EventTriggerType.FogoIgnitionLocked:
      return mapToFogoIgnitionLockedDTO(formValues as unknown as FogoIgnitionLockedTriggerFormValues);

    case EventTriggerType.FelixVanillaCollateral:
      return mapToFelixVanillaCollateralDTO(formValues as unknown as FelixVanillaCollateralTriggerFormValues);

    case EventTriggerType.FelixCdpCollateral:
      return mapToFelixCdpCollateralDTO(formValues as unknown as FelixCdpCollateralTriggerFormValues);

    case EventTriggerType.HypurrCardTransactions:
      return mapToHypurrCardTransactionsDTO(formValues as unknown as HypurrCardTransactionsTriggerFormValues);

    case EventTriggerType.PolymarketTradeVolume:
      return mapToPolymarketTradeVolumeDTO(formValues as unknown as PolymarketTradeVolumeTriggerFormValues);

    case EventTriggerType.Gearbox:
      return mapToGearboxTriggerDTO(formValues as unknown as GearboxTriggerFormValues);

    // Pendle triggers
    case EventTriggerType.Pendle:
      return mapToPendleYTTriggerDTO(formValues as unknown as PendleYTTriggerFormValues);

    case EventTriggerType.PendleLP:
      return mapToPendleLPTriggerDTO(formValues as unknown as PendleLPTriggerFormValues);

    // Spectra triggers
    case EventTriggerType.SpectraYT:
      return mapToSpectraYTTriggerDTO(formValues as unknown as SpectraYTTriggerFormValues);

    case EventTriggerType.SpectraLP:
      return mapToSpectraLPTriggerDTO(formValues as unknown as SpectraLPTriggerFormValues);

    // Social triggers
    case EventTriggerType.XFollowEvent:
      return mapToXFollowTriggerDTO(formValues as unknown as XFollowTriggerFormValues);

    case EventTriggerType.XPostEvent:
      return mapToXPostTriggerDTO(formValues as unknown as XPostTriggerFormValues);

    case EventTriggerType.DiscordServerJoin:
      return mapToDiscordServerJoinTriggerDTO(formValues as unknown as DiscordServerJoinTriggerFormValues);

    case EventTriggerType.DiscordRoleAssigned:
      return mapToDiscordRoleAssignedTriggerDTO(formValues as unknown as DiscordRoleAssignedTriggerFormValues);

    case EventTriggerType.StripePayment:
      return mapToStripePaymentDTO(formValues as unknown as StripePaymentTriggerFormValues);

    case EventTriggerType.XRepostEvent:
      return mapToXRepostTriggerDTO(formValues as unknown as XRepostTriggerFormValues);

    case EventTriggerType.XLikeEvent:
      return mapToXLikeTriggerDTO(formValues as unknown as XLikeTriggerFormValues);

    // Quest triggers
    case EventTriggerType.Zealy:
      return mapToZealyQuestTriggerDTO(formValues as unknown as ZealyTriggerFormValues);

    case EventTriggerType.Galxe:
      return mapToGalxeQuestTriggerDTO(formValues as unknown as GalxeTriggerFormValues);

    // Other triggers
    case EventTriggerType.Dune:
      return mapToDuneQueryTriggerDTO(formValues as unknown as DuneTriggerFormValues);

    case EventTriggerType.OnChainSubgraphEvent:
      return mapToSubgraphBalanceTriggerDTO(formValues as unknown as SubgraphBalanceTriggerFormValues);

    // Off-chain triggers
    case EventTriggerType.CustomEvent:
    case EventTriggerType.Github:
    case EventTriggerType.Discord:
      return mapToTriggerDTO(triggerType, formValues as unknown as OffChainTriggerFormValues);

    // On-chain custom triggers
    case EventTriggerType.OnChain:
    case EventTriggerType.OnChainFunction:
      return mapToTriggerDTO(
        triggerType,
        formValues as unknown as OnChainTriggerFormValues,
        formValues._contractIds as string[] | undefined,
        formValues as unknown as ConfigureTriggerFormValues,
      );

    default:
      throw new Error(`Encoder not implemented for trigger type: ${triggerType}`);
  }
};
